import { Link } from "react-router-dom";
import { ArrowUpRight, Film, Layers, Palette, ShieldCheck } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1761502479994-3a5e07ec243e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW92aWUlMjB0aGVhdGVyJTIwZW1wdHl8ZW58MHx8fHwxNzc3MTU1ODQwfDA&ixlib=rb-4.1.0&q=85";

export default function MarketingHome() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="marketing-home">
      {/* Sticky nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-[1280px] mx-auto h-16 px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketing-brand">
            <div className="w-7 h-7 bg-white grid place-items-center">
              <span className="text-black text-[10px] font-black tracking-tighter">VW</span>
            </div>
            <span className="font-display font-black tracking-tight text-lg">VAWWORLD</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.25em] text-white/60">
            <a href="#features" className="hover:text-white">Platform</a>
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="/api/docs" target="_blank" rel="noreferrer" className="hover:text-white">API</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white px-3 py-2"
              data-testid="marketing-signin-link"
            >
              Sign in
            </Link>
            <Link
              to="/login"
              className="text-xs uppercase tracking-[0.25em] bg-white text-black px-5 py-2.5 rounded-none hover:bg-amber-200 transition-colors inline-flex items-center gap-1.5"
              data-testid="marketing-cta-launch"
            >
              Launch your OTT <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/85 to-transparent" />
        </div>
        <div className="max-w-[1280px] mx-auto px-6 pt-28 pb-40">
          <div className="max-w-3xl">
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.3em] text-white/70"
              data-testid="marketing-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> A white-label OTT platform
            </span>
            <h1 className="mt-6 font-display font-black tracking-tighter text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.92]">
              Your studio.<br />
              <span className="text-white/40">Your audience.</span><br />
              Your <span className="italic font-medium">canon.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed font-light">
              Vawworld powers the next wave of independent streaming brands. Spin up a fully branded
              storefront, manage subscribers, and own your relationship with viewers — without
              renting space on someone else's marketplace.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/login"
                className="bg-white text-black px-8 py-4 rounded-none text-sm uppercase tracking-[0.25em] font-medium inline-flex items-center gap-2 hover:bg-amber-300 transition-colors"
                data-testid="marketing-hero-cta"
              >
                Launch your OTT <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/s/studio-aurora"
                className="px-8 py-4 rounded-none text-sm uppercase tracking-[0.25em] text-white/80 hover:text-white border border-white/15 hover:border-white/40 inline-flex items-center gap-2"
                data-testid="marketing-demo-aurora"
              >
                See a live storefront
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-[1280px] mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">01 / Platform</p>
            <h2 className="mt-3 font-display font-bold tracking-tighter text-4xl md:text-5xl max-w-2xl">
              Everything a streaming brand needs, nothing it doesn't.
            </h2>
          </div>
          <p className="text-sm text-white/50 max-w-sm font-light">
            Phase 1 today: tenants, identities, branding. Catalogue, payments and reels arrive in
            future phases — built on the same multi-tenant spine.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <FeatureCard
            className="md:col-span-3"
            icon={<Layers className="w-5 h-5" />}
            kicker="Multi-tenant"
            title="One platform. Many studios."
            body="Each studio gets its own slug, owner, subscribers and brand world — fully isolated, instantly provisioned."
          />
          <FeatureCard
            className="md:col-span-3"
            icon={<Palette className="w-5 h-5" />}
            kicker="White-label"
            title="Branding, surgically applied."
            body="Studio admins control name, tagline, logo, theme and accent colours. Storefronts repaint themselves on save."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<ShieldCheck className="w-5 h-5" />}
            kicker="3-role auth"
            title="Super, studio, viewer."
            body="Hardened JWT auth with role-scoped routes."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<Film className="w-5 h-5" />}
            kicker="Phase 2 ready"
            title="Catalogue spine."
            body="Schema designed for video, episodes and reels — without rewrites."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={<ArrowUpRight className="w-5 h-5" />}
            kicker="Open API"
            title="Built FastAPI-first."
            body="Every surface is documented at /api/docs. Hook in your own tools."
          />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-white/5">
        <div className="max-w-[1280px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-3 gap-10">
          <Step n="01" title="Provision a studio" body="A super admin creates the tenant, sets the brand, and hands the keys to the studio owner." />
          <Step n="02" title="Tune the brand" body="Studio admin tunes name, tagline, logo and colours. Live preview before publish." />
          <Step n="03" title="Open the doors" body="Subscribers sign up at /s/{slug}/signup and are scoped to that tenant. No leakage." />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12">
        <div className="max-w-[1280px] mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-white/80 grid place-items-center">
              <span className="text-black text-[8px] font-black">VW</span>
            </div>
            <span className="font-display font-bold tracking-tight">VAWWORLD</span>
            <span className="ml-3">© {new Date().getFullYear()}</span>
          </div>
          <div className="uppercase tracking-[0.3em]">Phase 1 — multi-tenant foundation</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, kicker, title, body, className = "" }) {
  return (
    <div
      className={`group relative bg-[#121212] border border-white/10 p-6 md:p-8 transition-all hover:border-white/30 hover:-translate-y-1 ${className}`}
      data-testid={`feature-card-${(kicker || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-white/60 group-hover:text-white transition-colors">{icon}</div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">{kicker}</span>
      </div>
      <h3 className="mt-8 font-display font-bold text-2xl md:text-3xl tracking-tight">{title}</h3>
      <p className="mt-3 text-sm text-white/55 font-light leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="border-t border-white/10 pt-6">
      <div className="text-[11px] uppercase tracking-[0.4em] text-white/30">Step {n}</div>
      <h4 className="mt-3 font-display font-bold text-2xl tracking-tight">{title}</h4>
      <p className="mt-3 text-sm text-white/55 font-light leading-relaxed">{body}</p>
    </div>
  );
}
